import React from 'react';
import './Button.css';

/**
 * Button — primary interactive primitive.
 *
 * Props:
 *   variant:  'primary' | 'secondary' | 'ghost' | 'danger' | 'brand-ghost'  (default 'secondary')
 *   size:     'sm' | 'md' | 'lg'                                             (default 'md')
 *   loading:  boolean — shows spinner, disables interaction
 *   leftIcon / rightIcon: ReactNode (lucide icons work great)
 *   fullWidth: boolean
 *   iconOnly: boolean — square button, use aria-label
 *   as:       'button' | 'a'                                                 (default 'button')
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  iconOnly = false,
  disabled,
  className = '',
  children,
  type = 'button',
  as: Component = 'button',
  ref,
  ...rest
}) {
  const classes = [
    'ui-btn',
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    fullWidth && 'ui-btn--block',
    iconOnly && 'ui-btn--icon-only',
    loading && 'ui-btn--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const props = {
    ref,
    className: classes,
    'aria-busy': loading || undefined,
    ...rest,
  };
  if (Component === 'button') {
    props.type = type;
    props.disabled = disabled || loading;
  }

  return (
    <Component {...props}>
      {loading && <span className="ui-btn__spinner" aria-hidden="true" />}
      {!loading && leftIcon && <span className="ui-btn__icon">{leftIcon}</span>}
      {!iconOnly && children != null && (
        <span className="ui-btn__label">{children}</span>
      )}
      {iconOnly && !loading && children}
      {!loading && rightIcon && <span className="ui-btn__icon">{rightIcon}</span>}
    </Component>
  );
}

export default Button;
